import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useShop } from '../contexts/ShopContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ShopSelector from './ShopSelector';

const Header: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { currentShop, userRole, currentMembership, canManageShop } = useShop();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthFlow = ['/signup', '/login', '/auth/action', '/accept-terms', '/forgot-password'].includes(location.pathname);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth < 640);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // Check if user has appropriate role for analysis features
  // NOTE: All tiers now have access to all features - usage is controlled by rate limits
  const canUseAnalysisFeatures = (() => {
    if (!currentShop || !currentMembership) return false;
    const allowedRoles = ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER'];
    return currentMembership.roles.some(role => allowedRoles.includes(role));
  })();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallMobile(window.innerWidth < 640);
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  return (
    <header
      style={{
        backgroundColor: 'var(--bg-card)',
        borderBottom: '1px solid var(--border-light)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        width: '100%'
      }}
      data-user-role={userRole || undefined}
    >
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 12px'
        }}
      >
        {/* Main Header Row - Always visible */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 0',
            gap: '8px'
          }}
        >
          {/* Logo - Compact on mobile */}
          <Link
            to="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textDecoration: 'none',
              color: 'inherit',
              flexShrink: 0
            }}
          >
            <img src="/logo-192.png" alt="Carousel Shop AI" width={isMobile ? 56 : 52} height={isMobile ? 56 : 52} style={{ borderRadius: '8px' }} />
            <div style={{ display: !isMobile ? 'block' : 'none' }}>
              <h1 style={{margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)'}}>Carousel Shop AI</h1>
            </div>
          </Link>

          {/* Shop Selector - Hidden on auth flow pages and very small screens */}
          {currentUser && !isAuthFlow && (
            <div style={{
              display: !isSmallMobile ? 'block' : 'none',
              flexGrow: 1,
              maxWidth: '300px'
            }}>
              <ShopSelector />
            </div>
          )}

          {/* Right side - Hamburger menu on mobile, user info on desktop */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
            {/* Desktop: Show user email and logout (hidden during auth flows) */}
            {currentUser && !isAuthFlow && (
              <>
                {/* Display Name/Email and Role - Hidden on mobile, shown on large screens */}
                <div style={{
                  display: isDesktop ? 'block' : 'none'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {currentUser.displayName || currentUser.email}
                  </span>
                  {userRole && (
                    <span
                      style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        backgroundColor: 'var(--bg-accent)',
                        color: 'var(--primary-dark)',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {userRole}
                    </span>
                  )}
                </div>

                {/* Profile gear icon - Hidden on mobile, shown on tablet+ */}
                <button
                  onClick={() => navigate('/profile')}
                  title="Profile Settings"
                  style={{
                    display: !isMobile ? 'flex' : 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border-medium)',
                    color: 'var(--delft-blue)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--delft-ice)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                </button>

                {/* Logout button - Hidden on mobile, shown on tablet+ */}
                <button
                  onClick={handleLogout}
                  style={{
                    display: !isMobile ? 'block' : 'none',
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border-medium)',
                    color: 'var(--text-primary)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Logout
                </button>
              </>
            )}

            {/* Mobile Menu Toggle - Only visible on mobile when authenticated */}
            {currentUser && isMobile && (
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label="Toggle menu"
              >
                {showMobileMenu ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu - Dropdown (only when authenticated) */}
        {currentUser && showMobileMenu && (
          <div
            style={{
              borderTop: '1px solid var(--border-light)',
              paddingTop: '12px',
              paddingBottom: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}
          >
            {/* Shop Selector - Show in mobile menu on small screens */}
            {currentUser && isSmallMobile && (
              <div style={{ marginBottom: '8px' }}>
                <ShopSelector />
              </div>
            )}

            {/* User Info - Show in mobile menu */}
            {currentUser && !isDesktop && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '4px',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  {currentUser.displayName || currentUser.email}
                </div>
                {userRole && (
                  <span
                    style={{
                      padding: '2px 8px',
                      backgroundColor: 'var(--bg-accent)',
                      color: 'var(--primary-dark)',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      flexShrink: 0
                    }}
                  >
                    {userRole}
                  </span>
                )}
              </div>
            )}

            {/* Profile link - Only show when authenticated */}
            {currentUser && (
              <button
                onClick={() => {
                  navigate('/profile');
                  setShowMobileMenu(false);
                }}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--delft-ice)',
                  border: '1px solid var(--border-medium)',
                  color: 'var(--delft-blue)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                  textAlign: 'left',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Profile Settings
              </button>
            )}

            {/* Logout - Show in mobile menu on small screens */}
            {currentUser && isMobile && (
              <button
                onClick={() => {
                  handleLogout();
                  setShowMobileMenu(false);
                }}
                style={{
                  padding: '12px',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-medium)',
                  color: 'var(--text-primary)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                  textAlign: 'left',
                  width: '100%'
                }}
              >
                Logout
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;